// In-memory stub store for the campaign studio. Everything here is fake and
// resets on reload; swap these functions for tRPC calls once the API exists.
import { reactive } from "vue";
import type {
  EmailBlock,
  EmailBlockType,
  EmailContent,
  SampleCustomer,
  StudioCampaign,
  StudioProduct,
  StudioSegment,
} from "./types";

export const newId = () => Math.random().toString(36).slice(2, 10);

// Self-contained placeholder art so previews never depend on external hosts.
export const placeholderImage = (label: string, from: string, to: string) =>
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="300" viewBox="0 0 600 300">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>
      </linearGradient></defs>
      <rect width="600" height="300" fill="url(#g)"/>
      <circle cx="480" cy="70" r="90" fill="#ffffff" fill-opacity="0.12"/>
      <circle cx="90" cy="260" r="120" fill="#ffffff" fill-opacity="0.08"/>
      <text x="300" y="165" font-family="Helvetica, Arial, sans-serif" font-size="38" font-weight="700"
        fill="#ffffff" text-anchor="middle">${label}</text>
    </svg>`,
  );

export const STUB_PRODUCTS: StudioProduct[] = [
  { id: "p1", name: "Flat White", price: 520, image: placeholderImage("Flat White", "#8b5e3c", "#d4a373") },
  { id: "p2", name: "Pistachio Croissant", price: 650, image: placeholderImage("Croissant", "#6a994e", "#a7c957") },
  { id: "p3", name: "Iced Matcha", price: 690, image: placeholderImage("Matcha", "#386641", "#90be6d") },
  { id: "p4", name: "Avocado Toast", price: 1450, image: placeholderImage("Avocado Toast", "#3a5a40", "#dad7cd") },
  { id: "p5", name: "Chai Latte", price: 590, image: placeholderImage("Chai", "#9c6644", "#e6ccb2") },
  { id: "p6", name: "Cinnamon Bun", price: 480, image: placeholderImage("Cinnamon Bun", "#bc6c25", "#fefae0") },
];

export const STUB_SEGMENTS: StudioSegment[] = [
  {
    id: "seg_lapsed",
    name: "Lapsed regulars",
    description: "5+ visits, none in the last 45 days",
    size: 412,
    reachable: { email: 318, whatsapp: 254, sms: 371, push: 142 },
  },
  {
    id: "seg_birthday",
    name: "Birthdays this month",
    description: "Customers with a birthday in the current month",
    size: 96,
    reachable: { email: 88, whatsapp: 61, sms: 79, push: 40 },
  },
  {
    id: "seg_new",
    name: "First-time visitors",
    description: "Exactly one visit in the last 30 days",
    size: 638,
    reachable: { email: 402, whatsapp: 297, sms: 455, push: 88 },
  },
  {
    id: "seg_vip",
    name: "VIP members",
    description: "Gold tier or top 5% by spend",
    size: 74,
    reachable: { email: 74, whatsapp: 66, sms: 70, push: 59 },
  },
  {
    id: "seg_all",
    name: "All subscribed customers",
    description: "Everyone who opted in to marketing",
    size: 2840,
    reachable: { email: 2210, whatsapp: 1530, sms: 2390, push: 610 },
  },
];

export const SAMPLE_CUSTOMERS: SampleCustomer[] = [
  {
    id: "c1",
    first_name: "Lena",
    last_name: "Meier",
    email: "lena.meier@example.ch",
    phone: "+41 79 123 45 67",
    points_balance: 340,
    favourite_item: "Flat White",
    locale: "de",
  },
  {
    id: "c2",
    first_name: "Julien",
    last_name: "Rochat",
    email: "julien.r@example.ch",
    phone: "+41 78 555 01 22",
    points_balance: 85,
    favourite_item: "Pistachio Croissant",
    locale: "fr",
  },
  {
    id: "c3",
    first_name: "Giulia",
    last_name: "Bernasconi",
    email: "giulia.b@example.ch",
    phone: "+41 76 902 11 08",
    points_balance: 1210,
    favourite_item: "Iced Matcha",
    locale: "it",
  },
];

export const STUB_SHOP = {
  name: "Café Lemmon",
  address: "Langstrasse 12, 8004 Zürich",
  logo: placeholderImage("Café Lemmon", "#1f2937", "#4b5563"),
};

// Per-message cost estimates (CHF cents) used by the review step.
export const CHANNEL_COST_CENTS: Record<string, number> = {
  email: 0.1,
  whatsapp: 7,
  sms: 9,
  push: 0,
};

export const defaultEmailBlock = (type: EmailBlockType): EmailBlock => {
  const id = newId();
  switch (type) {
    case "heading":
      return { id, type, text: "We miss you, {{first_name}}!", level: 1, align: "center" };
    case "text":
      return {
        id,
        type,
        text: "It's been a while since your last {{favourite_item}}. Come by this week and enjoy a little something on us.",
        align: "center",
      };
    case "image":
      return { id, type, src: placeholderImage("Autumn menu", "#e76f51", "#f4a261"), alt: "", href: "", width: 100 };
    case "button":
      return { id, type, label: "Book a table", href: "https://", align: "center", full_width: false };
    case "coupon":
      return { id, type, headline: "Your exclusive offer", show_qr: true, show_expiry: true };
    case "products":
      return { id, type, product_ids: ["p1", "p2", "p3"], columns: 3 };
    case "divider":
      return { id, type };
    case "spacer":
      return { id, type, height: 24 };
    case "social":
      return { id, type, instagram: "cafelemmon", facebook: "", website: "https://lemmon.ch" };
    case "footer":
      return { id, type, text: "{{shop_name}} · {{shop_address}}" };
  }
};

const defaultEmail = (): EmailContent => ({
  from_name: STUB_SHOP.name,
  reply_to: "hello@cafelemmon.ch",
  subject: "{{first_name}}, a coffee is waiting for you ☕",
  preheader: "Enjoy {{offer}} on your next visit",
  theme: {
    brand_color: "#e76f51",
    background_color: "#f4f1ea",
    content_background: "#ffffff",
    text_color: "#1f2937",
    font: "sans",
    radius: 12,
  },
  blocks: [
    { id: newId(), type: "image", src: placeholderImage("Café Lemmon", "#264653", "#2a9d8f"), alt: "", href: "", width: 100 },
    defaultEmailBlock("heading"),
    defaultEmailBlock("text"),
    defaultEmailBlock("coupon"),
    defaultEmailBlock("button"),
    defaultEmailBlock("products"),
    defaultEmailBlock("divider"),
    defaultEmailBlock("social"),
    defaultEmailBlock("footer"),
  ],
});

export const createDraftCampaign = (): StudioCampaign => ({
  id: newId(),
  name: "",
  status: "draft",
  segment_id: STUB_SEGMENTS[0].id,
  channels: ["email", "whatsapp"],
  strategy: "fallback",
  fallback_order: ["whatsapp", "push", "sms", "email"],
  holdout_percent: 10,
  offer: {
    type: "coupon",
    reward: "percentage",
    value: 15,
    product_id: null,
    min_spend: null,
    expires_in_days: 14,
    single_use: true,
  },
  schedule: { type: "now" },
  email: defaultEmail(),
  whatsapp: {
    template_name: "winback_offer",
    language: "de",
    header: { type: "image", src: placeholderImage("Café Lemmon", "#264653", "#2a9d8f") },
    body: "Hi {{first_name}} 👋\n\nWe haven't seen you in a while! Show this code at the counter for {{offer}} on your next visit:\n\n*{{coupon_code}}*\n\nValid until {{expiry_date}}.",
    footer: "Reply STOP to unsubscribe",
    buttons: [
      { id: newId(), type: "copy_code", label: "Copy code" },
      { id: newId(), type: "url", label: "See menu", url: "https://lemmon.ch/menu" },
    ],
    approval_status: "not_submitted",
  },
  sms: {
    sender_id: "CafeLemmon",
    body: "Hi {{first_name}}, we miss you! {{offer}} on your next visit with code {{coupon_code}}. Valid until {{expiry_date}}.",
    shorten_links: true,
    append_opt_out: true,
  },
  push: {
    title: "A treat is waiting ☕",
    body: "{{offer}} on your next visit — just show your pass.",
    update_pass_offer: true,
  },
  stats: null,
  updated_at: new Date().toISOString(),
});

const seeded = (patch: Partial<StudioCampaign>): StudioCampaign => ({
  ...createDraftCampaign(),
  ...patch,
});

const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString();

export const studioStore = reactive<{ campaigns: StudioCampaign[] }>({
  campaigns: [
    seeded({
      name: "Autumn win-back",
      status: "sent",
      segment_id: "seg_lapsed",
      channels: ["email", "whatsapp", "sms"],
      updated_at: daysAgo(6),
      stats: {
        targeted: 412,
        sent: 398,
        delivered: 391,
        opened: 244,
        clicked: 88,
        redeemed: 61,
        revenue: 148_900,
        unsubscribed: 4,
        by_channel: {
          whatsapp: { sent: 254, delivered: 251, opened: 219, clicked: 63 },
          sms: { sent: 92, delivered: 90, opened: 0, clicked: 14 },
          email: { sent: 52, delivered: 50, opened: 25, clicked: 11 },
        },
      },
    }),
    seeded({
      name: "Birthday treat — October",
      status: "sending",
      segment_id: "seg_birthday",
      channels: ["email", "push"],
      strategy: "all",
      updated_at: daysAgo(0),
      stats: {
        targeted: 96,
        sent: 58,
        delivered: 57,
        opened: 31,
        clicked: 9,
        redeemed: 6,
        revenue: 11_400,
        unsubscribed: 0,
        by_channel: {
          email: { sent: 40, delivered: 39, opened: 22, clicked: 7 },
          push: { sent: 18, delivered: 18, opened: 9, clicked: 2 },
        },
      },
    }),
    seeded({
      name: "Weekend brunch launch",
      status: "scheduled",
      segment_id: "seg_all",
      channels: ["email", "sms"],
      schedule: { type: "later", send_at: new Date(Date.now() + 3 * 86_400_000).toISOString(), send_in_customer_timezone: false },
      updated_at: daysAgo(1),
    }),
    seeded({
      name: "Second visit nudge",
      status: "draft",
      segment_id: "seg_new",
      channels: ["whatsapp"],
      updated_at: daysAgo(2),
    }),
  ],
});

export const getStudioCampaign = (id: string) =>
  studioStore.campaigns.find((c) => c.id === id) ?? null;

export const saveStudioCampaign = (campaign: StudioCampaign) => {
  const next = { ...campaign, updated_at: new Date().toISOString() };
  const index = studioStore.campaigns.findIndex((c) => c.id === campaign.id);
  if (index === -1) studioStore.campaigns.unshift(next);
  else studioStore.campaigns.splice(index, 1, next);
  return next;
};

export const deleteStudioCampaign = (id: string) => {
  studioStore.campaigns = studioStore.campaigns.filter((c) => c.id !== id);
};
